import { usePetEdit } from '@front/logic'
import { AppBackground, BottomActionContainer, Button, Input, Text, ResponsiveLogo, Drop } from '@ui/components'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { Image, TouchableOpacity, View } from 'react-native'
import Modal from 'react-native-modal'
import { loadSelectedPetAvatar, useAppDispatch, useAppSelector } from '@front/store'
import { ReactComponent as PawIcon } from '@/assets/icons/paw.svg'
import { ReactComponent as PawIconBlack } from '@/assets/icons/paw_color.svg'
import { ReactComponent as XSimpleIcon } from '@/assets/icons/x_simple_grey.svg'
import {
  BodyText,
  ButtonContainer,
  ErrorText,
  Headline5,
  InputContainer,
  InputWrapper,
  ModalContainer,
  ModalHeader,
  PreviewButtonContainer,
} from '@/aena/styles'
import { useNavigate } from 'react-router-dom'
import { CamaraCard, ImageTypeSelector } from '@/components/cameracard'
import { t } from 'i18next'
import { HeaderWithBackAena } from '@/components/headerwithback'
import { getPetBreedByType, type PetBreedInterface } from '@front/apis'
import CheckBoxCustom from '@/components/customcheckbox'



interface inputInterface {
  type: string
  label: string
  value: string
  handleChange: any
  handleBlur: any
  placeholder: string
  error?: string
}

export const PetEdit: FC = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const selectedPet = useAppSelector((state) => state.selectedPet)
  const { avatar, name } = selectedPet
  const [showModal, setShowModal] = React.useState<boolean>(false)
  const [showConfirmationModal, setShowConfirmationModal] = React.useState<boolean>(false)
  const [petBreeds, setPetBreeds] = useState<PetBreedInterface[]>([])

  const onNextPress = async () => {
    navigate(-1)
  }

  const { values, errors, handleBlur, handleChange, handleSubmit, deletePet, setFieldValue } = usePetEdit({ onNextPress })

  useEffect(() => {
    const fetchBreeds = async (): Promise<void> => {
      try {
        if (!selectedPet?.petType?.guid) return
        const res = await getPetBreedByType({ petTypeGUID: selectedPet.petType.guid })
        if (res.success) {
          setPetBreeds(res.data.petBreeds)
        }
      } catch (e) {
        // silent
      }
    }
    fetchBreeds()
  }, [selectedPet?.petType?.guid])

  const isCat = selectedPet?.petType?.name === 'Gato'
  const maxWeight = isCat ? 15 : 120
  const weightOptions = Array.from({ length: maxWeight }, (_, i) => ({ id: (i + 1).toString(), title: `${i + 1} kg`, data: i + 1 }))


  const onGalleryPress = (file: File, preview: string, type: ImageTypeSelector) => {
    setFieldValue('newAvatar', file)
    dispatch(loadSelectedPetAvatar(preview))
  }


  const handleConfirmationModal = () => {
    setShowConfirmationModal(!showConfirmationModal)
  }

  const handleDelete = () => {
    setShowConfirmationModal(false)
    deletePet().then(() => {
      setTimeout(() => navigate('/aena/home'), 100)

    })
  }

  console.log('values', values)

  return (
    <AppBackground isSafe>
      <View style={{ maxWidth: 800, width: '100%', alignSelf: 'center' }}>
        <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', padding: 16, gap: 16 }}>
          <ResponsiveLogo
            breakpoint={820}
            hysteresis={20}
            large={{ width: 680, height: 96 }}
            small={{ width: 360, height: 48 }}
          />
        </View>
        <HeaderWithBackAena title={t('common.editpet')} />
        <BottomActionContainer>
          <InputWrapper>
            <Headline5>{t('common.photo')} *</Headline5>
            <InputContainer>
              <CamaraCard
                title={t('common.choosephoto')}
                subtitle={t('common.upload_pet_photo')}
                onGalleryPress={onGalleryPress}
                url={avatar}
                type={ImageTypeSelector.EDIT_PET}
              />
            </InputContainer>

          </InputWrapper>
          <>            

          
            <InputWrapper>
              <Headline5>{t('common.name')}</Headline5>
              <InputContainer>
                <Input
                  inputProps={{
                    value: values.name,
                    onChangeText: handleChange('name'),
                    onBlur: handleBlur('name'),
                    placeholder: t('common.name'),
                  }}
                />
                {errors.name && (
                  <ErrorText>
                    {errors.name?.toString()}
                  </ErrorText>
                )}
              </InputContainer>
            </InputWrapper>
            <InputWrapper>
              <Headline5>Smart Pet Tag</Headline5>
              <InputContainer>
                <Input
                  inputProps={{
                    value: values.locatorActivationCode,
                    onChangeText: handleChange('locatorActivationCode'),
                    onBlur: handleBlur('locatorActivationCode'),
                    placeholder: t('common.addsmartpettag'),
                  }}
                />
                {errors.locatorActivationCode && (
                  <ErrorText>
                    {errors.locatorActivationCode?.toString()}
                  </ErrorText>
                )}
              </InputContainer>
            </InputWrapper>


            <View style={{ flexDirection: 'row', gap: 16 }}>
              <View style={{ flex: 1 }}>
                <InputWrapper>
                <Headline5>{t('common.weigth')} *</Headline5>
                <InputContainer>
                  <Drop
                    data={weightOptions}
                    active={weightOptions.findIndex((obj) => obj.data === values.weight)}
                    selectedItem={weightOptions.findIndex((obj) => obj.data === values.weight)}
                    onPress={(item) => { setFieldValue('weight', item.data); handleBlur('weight') }}
                    placeholder={'0 kg'}
                    icon={<PawIconBlack />}
                    color="aena"
                    buttonText={t('common.continue')}
                    variant="outlined"
                  />
                </InputContainer>
                </InputWrapper>
              </View>
              <View style={{ flex: 1 }}>
              <InputWrapper>

              <Headline5>{t('common.breed')} *</Headline5>
              <InputContainer>
                <Drop
                  data={petBreeds.map((breed) => ({ id: breed.guid, title: breed.name, data: breed }))}
                  active={petBreeds.findIndex((b) => b.guid === values.petBreed)}
                  selectedItem={petBreeds.findIndex((b) => b.guid === values.petBreed)}
                  onPress={(item) => { setFieldValue('petBreed', item.id); handleBlur('petBreed') }}
                  placeholder={t('common.selectabreed')}
                  icon={<PawIconBlack />}
                  variant="outlined"
                  buttonText={t('common.continue')}
                  color="aena"
                />
              </InputContainer>                
              </InputWrapper>
              </View>


            </View>
            <View style={{ flexDirection: 'row', gap: 16 }}>
              <View style={{ flex: 1 }}>
              <Headline5>{t('common.sterilized')} *</Headline5>
              <InputContainer>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <CheckBoxCustom
                      isChecked={ values.isSterilised === true }
                      onToggle={() => { setFieldValue('isSterilised', true); handleBlur('isSterilised') }}
                    />
                    <BodyText>{t('common.yes')}</BodyText>  
                  </View>
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <CheckBoxCustom
                      isChecked={ values.isSterilised === false }
                      onToggle={() => { setFieldValue('isSterilised', false); handleBlur('isSterilised') }}
                    />
                    <BodyText>{t('common.no')}</BodyText>
                  </View>
                </View>
              </InputContainer>       
              </View>
              <View style={{ flex: 1 }}>
                <Headline5>{t('common.sex')} *</Headline5>
                <InputContainer>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <CheckBoxCustom
                        isChecked={values.gender === 'MALE'}
                        onToggle={() => { setFieldValue('gender', 'MALE'); handleBlur('gender') }}
                      />
                      <BodyText>{t('common.male')}</BodyText>
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <CheckBoxCustom
                        isChecked={values.gender === 'FEMALE'}
                        onToggle={() => { setFieldValue('gender', 'FEMALE'); handleBlur('gender') }}
                      />
                      <BodyText>{t('common.female')}</BodyText>
                    </View>
                  </View>
                </InputContainer>
            </View>                     
            </View>


          </>            
          <Button
              title={t('common.update')}
              buttonWrapperStyle={{ backgroundColor: '#96ce00' }}
              icon={<PawIcon width={24} height={24} />}
              size="block"
              textType="cta_medium"
              borderRadius={5}
              textColor="background0"
              allowIconAndText
              color="primary"
              onPress={handleSubmit}
            />          
        </BottomActionContainer>
        <div style={{ padding: 15 }}>
          <Button
            title={t('common.delete')}
            buttonWrapperStyle={{ backgroundColor: '#ce1f00ff' }}
            icon={<PawIcon width={24} height={24} />}
            size="block"
            textType="cta_medium"
            borderRadius={5}
            textColor="background0"
            allowIconAndText
            color="error"
            onPress={handleConfirmationModal}
          />
        </div>

        <Modal
          isVisible={showConfirmationModal}
          backdropOpacity={0.7}
          useNativeDriver
          useNativeDriverForBackdrop
          hideModalContentWhileAnimating
          onBackdropPress={() => setShowConfirmationModal(!showConfirmationModal)}>
          <ModalContainer>
            <ModalHeader>
              <Text type="H4" textAlign="center">
                {t('common.areyousureyouwanttodeletename', { name })}
              </Text>
            </ModalHeader>
            <Text type="H5" textAlign="center">
              {t('common.onceyoudeletenameyoucannotrecovertheinformation', { name })}
            </Text>
            <ButtonContainer>
              <Button
                title={t('common.cancel')}
                buttonWrapperStyle={{ backgroundColor: '#96ce00' }}
                icon={<PawIcon width={24} height={24} />}
                size="small"
                textType="cta_medium"
                borderRadius={5}
                textColor="background0"
                allowIconAndText
                color="primary"
                onPress={handleConfirmationModal}
              />
              <Button
                title={t('common.delete')}
                buttonWrapperStyle={{ backgroundColor: '#ce1f00ff' }}
                icon={<PawIcon width={24} height={24} />}
                size="small"
                textType="cta_medium"
                borderRadius={5}
                textColor="background0"
                allowIconAndText
                color="error"
                onPress={handleDelete}
              />
            </ButtonContainer>
          </ModalContainer>
        </Modal>
      </View>

    </AppBackground>
  )
}
